import { ReactNode, useEffect, useRef } from 'react';

/**
 * Modal — dialog overlay. Native HTML <dialog> element for built-in focus trap +
 * Escape-to-close + modal backdrop.
 *
 * Derived from M3 Dialog (Basic + Full-Screen variants) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/dialogs/overview
 *   - Reference: github.com/material-components/material-web/tree/main/dialog
 *
 * Populus adaptations applied:
 *   - Native <dialog> (modern browser support; Chrome 37+, Firefox 98+, Safari 15.4+).
 *     M3 reference impl uses a custom Lit element; native gives focus trap +
 *     Escape-key handling + backdrop click semantics for free.
 *   - Size variants: sm (confirm dialogs; max-width 400px) + lg (form dialogs;
 *     max-width 720px) + full (full-screen on mobile per M3 Full-Screen Dialog spec)
 *   - Backdrop click closes (M3 default + Populus convention)
 *   - Footer slot for action buttons (right-aligned per M3 Dialog spec)
 *
 * Token discipline:
 *   - Modal surface → --color-card (system)
 *   - Border → --color-border (system)
 *   - Shadow → --sh-modal (system; M3 elevation level 5)
 *   - Action buttons inside render their own brand-primary via Button atom
 *
 * Phase 11 Block 6 PR 3c (2026-05-12).
 */

export type ModalSize = 'sm' | 'lg' | 'full';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeOnBackdropClick?: boolean;
}

const sizeStyles: Record<ModalSize, React.CSSProperties> = {
  sm: { maxWidth: 400, width: '90vw' },
  lg: { maxWidth: 720, width: '90vw' },
  full: { maxWidth: '95vw', width: '95vw', height: '90vh' },
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  closeOnBackdropClick = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Sync open prop with native <dialog> imperative API
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Bridge native <dialog>'s close event → React onClose
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  // Backdrop click — native <dialog> doesn't differentiate backdrop from
  // dialog body without manual hit-testing on the dialog element itself
  const handleClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (!closeOnBackdropClick) return;
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClick={handleClick}
      style={{
        ...sizeStyles[size],
        padding: 0,
        border: 'none',
        borderRadius: 'var(--r-2xl)',
        background: 'var(--color-card)',
        color: 'var(--color-ink)',
        boxShadow: 'var(--sh-modal)',
        overflow: 'hidden',
      }}
    >
      {title && (
        <header style={{
          padding: 'var(--sp-4) var(--sp-5)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink)',
          }}>{title}</h2>
        </header>
      )}
      <div style={{
        padding: 'var(--sp-5)',
        overflow: 'auto',
        maxHeight: size === 'full' ? 'calc(90vh - 140px)' : '70vh',
      }}>
        {children}
      </div>
      {footer && (
        <footer style={{
          padding: 'var(--sp-3) var(--sp-5)',
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--sp-2)',
        }}>
          {footer}
        </footer>
      )}
    </dialog>
  );
}
